import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Button } from '@pijma/crypto'

import { EPaymentMethod } from '@api/types'
import { useToken } from '@hooks'
import { RootState } from '@stores'
import { ECurrency, isoToECurrency, TPayFormState } from '@stores/types/*'
import { extractCurrencySymbol, localeNumber } from '@utils'
import styled from 'styled-components'

import { TPaymentGateway } from '../../../../stores/types/TPaymentGatewayState'
import { H4 } from '../../../common/header/Headings'
import { Island } from '../../../common/island'
import NavigationBar from '../../../common/navigation/NavigationBar'
import { BodyText500, CaptionText500 } from '../../../common/text/BodyText'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 20px;
`

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 24px 20px 20px;
`

const SourceTitle = styled.div`
  display: flex;
  flex-direction: column;
  white-space: pre-wrap;
  width: 100%;
  padding: 20px 24px 16px;
`

const SumInfo = styled.div`
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const MethodInfo = styled.div`
  padding: 8px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
`

const NavigationBarWrapper = styled.div`
  padding: 0 4px;
  display: flex;
  width: 100%;
  align-items: start;
`

const BackContainer = styled.div`
  margin: 24px 0 0 0;
  color: #217aac;

  span {
    cursor: pointer;

    :hover {
      color: rgb(49, 49, 49);
    }
  }
`

interface StateProps {
  payForm: TPayFormState
  paymentGateway?: TPaymentGateway
}

interface SellData {
  key: string
  value: string
}

const SellFinalPage = (props: StateProps) => {
  const intl = useIntl()
  const redirectTo = useNavigate()

  if (!props.payForm.amountFrom || !+props.payForm.amountFrom) {
    redirectTo('../sum')
  }

  const buildSellInfo = () => {
    const currencyTo = isoToECurrency(props.paymentGateway?.currencyTo)
    let entries: SellData[] = []

    switch (currencyTo) {
      case ECurrency.INR:
        entries = [
          { key: 'sellFinalPage.method', value: EPaymentMethod.IMPS },
          {
            key: 'sellFinalPage.account',
            value: props.payForm.impsAccount || '',
          },
          {
            key: 'sellFinalPage.accountNum',
            value: props.payForm.impsAccountNum || '',
          },
          { key: 'sellFinalPage.ifsc', value: props.payForm.ifsc || '' },
        ]
        break

      case ECurrency.VND:
      case ECurrency.THB:
        entries = [
          {
            key: 'sellFinalPage.bank',
            value: props.payForm.receiverBank?.name || '',
          },
          {
            key: 'sellFinalPage.accountHolder',
            value: props.payForm.receiverAccountHolder || '',
          },
          {
            key: 'sellFinalPage.accountNumber',
            value: props.payForm.receiverAccountNumber || '',
          },
        ]
        break
    }

    return entries
  }

  const goToFindOffersPage = () => {
    redirectTo('../find-offers')
  }

  const currencyTo = extractCurrencySymbol(
    props.payForm.paymentGateway?.currencyTo,
  )

  const currencyFrom =
    extractCurrencySymbol(props.payForm.paymentGateway?.currencyFrom) ===
    ECurrency.USDT_TRC20
      ? 'USDT'
      : extractCurrencySymbol(props.payForm.paymentGateway?.currencyFrom)

  const prettyRate = props.payForm?.rate as number

  const disabledColor = useToken().color.font.light_bg.secondary.default.value

  return (
    <Container>
      <NavigationBarWrapper>
        <NavigationBar
          dataRedirectLink={`../source/${props.payForm.paymentGateway?.paymentMethod}`}
          sumRedirectLink={`../sum`}
          paymentDisabled
          showMethod
          methodRedirectLink={'../method'}
          noPaymentMethod
        />
      </NavigationBarWrapper>
      <Island size="M" bgColor="primary">
        <SourceTitle>
          <H4>{intl.formatMessage({ id: 'sellFinalPage.title' })}</H4>
        </SourceTitle>
        <SumInfo>
          <Row>
            <CaptionText500 color={disabledColor}>
              {intl.formatMessage({ id: 'sellFinalPage.sell' })}
            </CaptionText500>
            <CaptionText500>
              {localeNumber(props.payForm.amountFrom || '')} {currencyFrom}
            </CaptionText500>
          </Row>
          <Row>
            <CaptionText500 color={disabledColor}>
              {intl.formatMessage({ id: 'sellFinalPage.get' })}
            </CaptionText500>
            <CaptionText500>
              ~{localeNumber(props.payForm.amountTo || '')} {currencyTo}
            </CaptionText500>
          </Row>
          <Row>
            <CaptionText500 color={disabledColor}>
              {intl.formatMessage(
                { id: 'sellFinalPage.rate' },
                { currency: currencyFrom },
              )}
            </CaptionText500>
            <CaptionText500>
              {localeNumber(prettyRate + '')} {currencyTo}
            </CaptionText500>
          </Row>
        </SumInfo>
        <MethodInfo>
          {buildSellInfo().map((info) => (
            <Row key={info.key}>
              <CaptionText500 color={disabledColor}>
                {intl.formatMessage({ id: info.key })}
              </CaptionText500>
              <CaptionText500>{info.value}</CaptionText500>
            </Row>
          ))}
        </MethodInfo>
        <ButtonContainer>
          <Button
            width={'100%'}
            size={'m'}
            onPress={() => goToFindOffersPage()}
          >
            {intl.formatMessage({ id: 'sellFinalPage.button' })}
          </Button>
        </ButtonContainer>
      </Island>
      <BackContainer onClick={() => redirectTo('../sum')}>
        <BodyText500>
          {intl.formatMessage({ id: 'sellFinalPage.back' })}
        </BodyText500>
      </BackContainer>
    </Container>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  payForm: state.payForm,
  paymentGateway: state.payForm?.paymentGateway,
})

export default connect(mapStateToProps)(SellFinalPage)
